import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PageTitle } from '../../components/PageTitle';
import { useBlogPost } from '../../hooks/useBlog';
import PortableText from '../../components/PortableText';
import { getHreflangForPage } from '../../config/international';

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const { post, loading, error } = useBlogPost(slug || '');

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Post Not Found</h2>
          <p className="text-gray-600 mb-4">
            {error || 'The blog post you are looking for does not exist.'}
          </p>
          <Link 
            to="/blog"
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageTitle 
        title={`${post.title} - CodeSplash Blog`}
        description={post.excerpt}
        canonicalUrl={`https://codesplashdesigns.com/blog/${post.slug.current}`}
        hreflang={getHreflangForPage(`/blog/${post.slug.current}`)}
        language="en"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-primary to-blue-700 text-white py-16">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="flex items-center gap-2 mb-4">
              {post.categories.map((category: string, index: number) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-white/20 text-white text-sm font-medium rounded-full"
                >
                  {category}
                </span>
              ))}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              {post.title}
            </h1>
            
            <p className="text-xl mb-8 text-gray-100">
              {post.excerpt}
            </p>
            
            <div className="flex items-center gap-4 text-gray-100">
              <div className="flex items-center gap-2">
                {post.authorImage && (
                  <img
                    src={post.authorImage}
                    alt={post.author}
                    className="w-10 h-10 rounded-full"
                  />
                )}
                <span className="font-medium">{post.author}</span>
              </div>
              <span>•</span>
              <span>{formatDate(post.publishedAt)}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Main Image */}
      {post.mainImage && (
        <section className="py-8">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <img
                  src={post.mainImage}
                  alt={post.title}
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Blog Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="prose prose-lg max-w-none"
            >
              <PortableText content={post.body} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Author Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="bg-white rounded-lg p-8 shadow-lg"
            >
              <div className="flex items-center gap-6">
                {post.authorImage && (
                  <img
                    src={post.authorImage}
                    alt={post.author}
                    className="w-20 h-20 rounded-full"
                  />
                )}
                <div>
                  <h3 className="text-2xl font-bold mb-2">About {post.author}</h3>
                  <p className="text-gray-600">
                    Software developer and technology enthusiast. Passionate about creating innovative solutions that help businesses grow and succeed in the digital age.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related Posts Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <h2 className="text-3xl font-bold mb-8 text-center">More from Our Blog</h2>
              <div className="text-center">
                <Link
                  to="/blog"
                  className="bg-primary text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                >
                  View All Posts
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-gray-600 mb-8">
              Get the latest insights on software development, industry trends, and tips delivered to your inbox.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <button
                type="submit"
                className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost; 